/**
 * Aspect-ratio helpers for media display.
 *
 * The card carousel and inline media previews use a single resolved aspect
 * ratio per group of items so heights do not jump between snaps. To keep the
 * card from dominating the viewport (mobile) or stretching too wide (desktop),
 * resolved ratios are clamped to a sensible band and items render with
 * `object-contain` against that bounded frame.
 *
 * Pure helpers here are platform-agnostic so they can move to a shared
 * package alongside React Native consumers later (rule 18).
 */

/**
 * Bounded portrait edge of the card frame (3:4). Anything taller is letterboxed
 * inside the bounded frame rather than forcing the whole card portrait.
 * MediaViewer uses a looser bound to allow full-screen portrait media.
 */
export const MIN_CARD_ASPECT = 3 / 4;
export const MIN_VIEWER_ASPECT = 9 / 16;

/**
 * Bounded landscape edge of the card frame (16:9). Anything wider is
 * letterboxed inside the bounded frame.
 */
export const MAX_CARD_ASPECT = 16 / 9;
export const MAX_VIEWER_ASPECT = 16 / 9;

/**
 * Initial neutral aspect used before any item has reported its intrinsic
 * dimensions. 4:5 reads as a typical social-media post and avoids the very
 * tall 9:16 layout shift that the legacy carousel produced.
 */
export const DEFAULT_CARD_ASPECT = 4 / 5;

/**
 * Aspect ratio used by the full-screen reels surface cell. Each cell is
 * 100dvh; the inner media is `object-contain` so vertical and horizontal
 * orientations both display fully.
 */
export const REELS_CELL_ASPECT = 9 / 16;

/**
 * Numeric guards. Aspect ratios outside this range are treated as missing
 * because they almost certainly indicate a parsing bug rather than a real
 * intrinsic size.
 */
const SANE_MIN = 0.05;
const SANE_MAX = 20;

/**
 * Returns true when the supplied ratio is a positive finite number within
 * sane bounds. Use this before forwarding client-measured dimensions to the
 * server.
 */
export function isSaneAspectRatio(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isFinite(value) &&
    value >= SANE_MIN &&
    value <= SANE_MAX
  );
}

/**
 * Clamp a measured aspect ratio into the bounded card frame.
 */
export function clampAspectRatio(
  ratio: number,
  min: number = MIN_CARD_ASPECT,
  max: number = MAX_CARD_ASPECT,
): number {
  if (!Number.isFinite(ratio) || ratio <= 0) {
    return DEFAULT_CARD_ASPECT;
  }
  if (ratio < min) return min;
  if (ratio > max) return max;
  return ratio;
}

/**
 * Median of a non-empty list of numbers. Used to pick a single aspect for a
 * group of mixed-orientation items so the carousel does not snap-jump.
 */
function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[middle - 1] + sorted[middle]) / 2;
  }
  return sorted[middle];
}

/**
 * Resolve a single aspect ratio for a group of items. Returns the clamped
 * median of valid ratios, or the supplied fallback when nothing is known yet.
 */
export function resolveGroupAspect(
  ratios: ReadonlyArray<number | null | undefined>,
  fallback: number = DEFAULT_CARD_ASPECT,
): number {
  const valid = ratios.filter(isSaneAspectRatio);
  if (valid.length === 0) {
    return clampAspectRatio(fallback);
  }
  return clampAspectRatio(median(valid));
}

/**
 * Build the inline `style.aspectRatio` value from a numeric ratio. Tailwind's
 * `aspect-[]` arbitrary values cannot interpolate at build time, so the
 * carousel applies the resolved ratio inline.
 */
export function aspectRatioStyle(ratio: number): { aspectRatio: string } {
  return { aspectRatio: `${ratio}` };
}

/**
 * Result of measuring an upload's intrinsic dimensions.
 */
export interface IntrinsicDimensions {
  width: number;
  height: number;
  aspectRatio: number;
}

/**
 * Build an `IntrinsicDimensions` object from a width/height pair, returning
 * `null` when either dimension is non-positive.
 */
export function buildIntrinsicDimensions(
  width: number,
  height: number,
): IntrinsicDimensions | null {
  if (
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0
  ) {
    return null;
  }
  const aspectRatio = parseFloat((width / height).toFixed(4));
  if (!isSaneAspectRatio(aspectRatio)) {
    return null;
  }
  return { width: Math.round(width), height: Math.round(height), aspectRatio };
}

/**
 * Try to derive intrinsic dimensions from an SVG file's text body by parsing
 * the `viewBox` attribute and falling back to numeric `width`/`height`
 * attributes. Returns `null` when nothing parseable is present.
 */
function parseSvgDimensionsFromText(text: string): IntrinsicDimensions | null {
  const viewBoxMatch = text.match(/viewBox\s*=\s*["']([^"']+)["']/i);
  if (viewBoxMatch) {
    const parts = viewBoxMatch[1].trim().split(/[\s,]+/);
    if (parts.length === 4) {
      const width = parseFloat(parts[2]);
      const height = parseFloat(parts[3]);
      const dims = buildIntrinsicDimensions(width, height);
      if (dims) return dims;
    }
  }
  // Require a numeric value followed by the closing quote (with optional
  // unit like `px`/`pt`) so percentage / `auto` values do not falsely
  // match. Percentages cannot be turned into a fixed aspect ratio without
  // additional layout context.
  const widthMatch = text.match(/\bwidth\s*=\s*["']([0-9.]+)(?:px|pt)?["']/i);
  const heightMatch = text.match(/\bheight\s*=\s*["']([0-9.]+)(?:px|pt)?["']/i);
  if (widthMatch && heightMatch) {
    return buildIntrinsicDimensions(
      parseFloat(widthMatch[1]),
      parseFloat(heightMatch[1]),
    );
  }
  return null;
}

const SVG_TEXT_PARSE_BUDGET_BYTES = 256 * 1024;

/**
 * Read an SVG file as text (capped) and parse its dimensions. Web-only.
 */
async function extractSvgIntrinsic(
  file: File,
): Promise<IntrinsicDimensions | null> {
  try {
    const slice = file.slice(0, SVG_TEXT_PARSE_BUDGET_BYTES);
    const text = await slice.text();
    return parseSvgDimensionsFromText(text);
  } catch {
    return null;
  }
}

const IMAGE_DECODE_TIMEOUT_MS = 4000;
const VIDEO_DECODE_TIMEOUT_MS = 6000;

/**
 * Decode an image File via a transient `Image` element to read
 * `naturalWidth`/`naturalHeight`. Web-only.
 */
function extractImageIntrinsic(
  file: File,
): Promise<IntrinsicDimensions | null> {
  return new Promise((resolve) => {
    if (typeof window === "undefined") {
      resolve(null);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    let settled = false;
    const finish = (dims: IntrinsicDimensions | null): void => {
      if (settled) return;
      settled = true;
      URL.revokeObjectURL(objectUrl);
      resolve(dims);
    };
    const timer = window.setTimeout(
      () => finish(null),
      IMAGE_DECODE_TIMEOUT_MS,
    );
    img.onload = () => {
      window.clearTimeout(timer);
      finish(buildIntrinsicDimensions(img.naturalWidth, img.naturalHeight));
    };
    img.onerror = () => {
      window.clearTimeout(timer);
      finish(null);
    };
    img.src = objectUrl;
  });
}

/**
 * Decode a video File via a transient `<video>` element to read
 * `videoWidth`/`videoHeight`. Web-only.
 */
function extractVideoIntrinsic(
  file: File,
): Promise<IntrinsicDimensions | null> {
  return new Promise((resolve) => {
    if (typeof document === "undefined") {
      resolve(null);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;
    let settled = false;
    const finish = (dims: IntrinsicDimensions | null): void => {
      if (settled) return;
      settled = true;
      URL.revokeObjectURL(objectUrl);
      resolve(dims);
    };
    const timer = window.setTimeout(
      () => finish(null),
      VIDEO_DECODE_TIMEOUT_MS,
    );
    video.onloadedmetadata = () => {
      window.clearTimeout(timer);
      finish(buildIntrinsicDimensions(video.videoWidth, video.videoHeight));
    };
    video.onerror = () => {
      window.clearTimeout(timer);
      finish(null);
    };
    video.src = objectUrl;
  });
}

/**
 * Best-effort intrinsic dimension extractor for an upload File. Uses the
 * appropriate browser API for the MIME type and times out after a few
 * seconds so a flaky decode never blocks the upload pipeline.
 *
 * Returns `null` when nothing parseable could be read; callers must fall
 * back to live measurement on render.
 */
export async function extractIntrinsicDimensions(
  file: File,
): Promise<IntrinsicDimensions | null> {
  const mime = (file.type || "").toLowerCase();
  if (mime === "image/svg+xml" || /\.svg$/i.test(file.name)) {
    return extractSvgIntrinsic(file);
  }
  if (mime.startsWith("video/")) {
    return extractVideoIntrinsic(file);
  }
  if (mime.startsWith("image/")) {
    return extractImageIntrinsic(file);
  }
  return null;
}

/**
 * Test-only export: SVG text parsing helper. Kept named so unit tests do not
 * have to construct a `File` to validate parsing logic.
 */
export const __test__ = { parseSvgDimensionsFromText };
